import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Modal, Button, Form } from "react-bootstrap";
import { PostRequest } from "../../Utils/hooks/ApiHandler";
import { baseURL } from "../../Utils/constants/constant";
import { useNavigate } from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";
import { toast } from "sonner";

// Validation Schema
const twilioSchema = z.object({
  account_sid: z.string().min(10, "Account SID is required"),
  auth_token: z.string().min(10, "Auth Token is required"),
  api_key: z.string().min(10, "API Key is required"),
  api_secret: z.string().min(10, "API Secret is required"),
  twiml_app_sid: z.string().min(10, "TwiML App SID is required"),
  twilio_number: z.string().regex(/^\+\d+$/, "Required format: +1234567890"),
});
const TwilioIntegrationForm = ({ isOpen, onClose, }) => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  });
  const [internalSubmitting, setInternalSubmitting] = useState(false);
  const customerId = customer?.customer_details?.customer_id;
  const token = customer?.token?.authorization;
  const rf = customer?.token?.authentication;
  const headers = {
    authentication: rf,
    Authorization: token,
  };
  const extras = { customer, setCustomer, rf, navigate };
  const {
    register,
    handleSubmit,
    formState: { errors,isSubmitting },
    reset,
  } = useForm({
    resolver: zodResolver(twilioSchema),
    defaultValues: {
      account_sid: "",
      auth_token: "",
      api_key: "",
      api_secret: "",
      twiml_app_sid: "",
      twilio_number: ""
    }
  });


  const onValidate = async (data) => {
    setInternalSubmitting(true);
    try {
      const payload = {
        customer_id: customerId,
        account_sid: data.account_sid,
        auth_token: data.auth_token,
        api_key: data.api_key,
        api_secret: data.api_secret,
        app_sid: data.twiml_app_sid,
        number: data.twilio_number,
        identity: data.twilio_number,
        user_login_id: customer?.customer_details?.id
      };

      const res = await PostRequest(
        `${baseURL}voice/insertTwilioCredentials`, 
        payload, 
        false, 
        {headers}, 
        extras
      );

      if (res.status === "fail" || res.status === false) {
        return toast.error(res?.message || "Something went wrong");
      }

      toast.success("Twilio Connected Successfully");
      reset(); 
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Failed to connect Twilio");
    } finally {
      setInternalSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal 
      show={isOpen} 
      onHide={handleClose} 
      size="lg" 
      centered
      backdrop="static" 
      keyboard={false} 
    >
      {/* Header */}
      <Modal.Header 
        closeButton 
        className="bg-primary text-white"
        style={{ 
          borderTopLeftRadius: "15px", 
          borderTopRightRadius: "15px",
          borderBottom: "none"
        }}
      >
        <Modal.Title className="fw-bold">
          <i className="bi bi-gear-fill me-2"></i>
          Twilio Integration
        </Modal.Title>
      </Modal.Header>

      {/* Body */}
      <Modal.Body className="p-4">
        <p className="text-muted mb-4 small">
          Your Twilio integration is missing. Please enter your credentials to enable the dialer.
        </p>

        <Form onSubmit={handleSubmit(onValidate)}>
          <div className="row g-3">
            {/* Account SID */}
            <div className="col-md-6">
              <Form.Group controlId="account_sid">
                <Form.Label className="small fw-bold">Account SID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="ACxxxxxxxxxxxxxxxx"
                  isInvalid={!!errors.account_sid}
                  {...register("account_sid")}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.account_sid?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            {/* Auth Token */}
            <div className="col-md-6">
              <Form.Group controlId="auth_token">
                <Form.Label className="small fw-bold">Auth Token</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Auth Token"
                  isInvalid={!!errors.auth_token}
                  {...register("auth_token")}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.auth_token?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            {/* API Key */}
            <div className="col-md-6">
              <Form.Group controlId="api_key">
                <Form.Label className="small fw-bold">API Key</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="SKxxxxxxxxxxxxxxxx"
                  isInvalid={!!errors.api_key}
                  {...register("api_key")}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.api_key?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            {/* API Secret */}
            <div className="col-md-6">
              <Form.Group controlId="api_secret">
                <Form.Label className="small fw-bold">API Secret</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="API Secret"
                  isInvalid={!!errors.api_secret}
                  {...register("api_secret")}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.api_secret?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            {/* TwiML App SID */}
            <div className="col-md-6">
              <Form.Group controlId="twiml_app_sid">
                <Form.Label className="small fw-bold">TwiML App SID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="APxxxxxxxxxxxxxxxx"
                  isInvalid={!!errors.twiml_app_sid}
                  {...register("twiml_app_sid")}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.twiml_app_sid?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            {/* Twilio Number */}
            <div className="col-md-6">
              <Form.Group controlId="twilio_number">
                <Form.Label className="small fw-bold">Twilio Phone Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="+1234567890"
                  isInvalid={!!errors.twilio_number}
                  {...register("twilio_number")}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.twilio_number?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>
        </Form>
      </Modal.Body>

      {/* Footer */}
      <Modal.Footer className="border-0 pt-0">
        <div className="d-flex gap-2 w-100">
          <Button
            variant="light"
            className="flex-fill py-2 fw-bold border"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="flex-fill py-2 fw-bold"
            onClick={handleSubmit(onValidate)}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Connecting...
              </>
            ) : (
              "Connect & Save"
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default TwilioIntegrationForm;